import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { CheckCircle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // processing, success, error, timeout
  const [message, setMessage] = useState('Confirming your payment...');
  const [purchase, setPurchase] = useState(null); // { type, product } when FirstLook
  const pollCount = useRef(0);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');

    if (!sessionId) {
      setStatus('error');
      setMessage('Missing payment session information.');
      return;
    }

    const maxAttempts = 6;
    const pollInterval = 2500;

    const poll = async () => {
      if (pollCount.current >= maxAttempts) {
        setStatus('timeout');
        setMessage('We are still confirming your payment. You will receive a confirmation email shortly.');
        return;
      }
      pollCount.current += 1;

      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.paymentStatus === 'paid') {
          if (res.data.type === 'firstlook') {
            setPurchase({ type: 'firstlook', product: res.data.product });
            setMessage(res.data.product === 'remote'
              ? 'Payment received! Your Vehicle Decision Report is unlocked.'
              : 'Payment received! Your Full Inspection is booked.');
          } else {
            setMessage('Payment received! Your inspection appointment is now confirmed.');
          }
          setStatus('success');
          return;
        }
        if (res.data.status === 'expired') {
          setStatus('error');
          setMessage('This payment session has expired. Please contact us to receive a new payment link.');
          return;
        }
        setTimeout(poll, pollInterval);
      } catch (error) {
        console.error('Payment status error:', error);
        setTimeout(poll, pollInterval);
      }
    };

    poll();
  }, [searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #141416 0%, #1f1f23 50%, #161618 100%)' }}>
      <div className="max-w-md w-full mx-4">
        <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl border border-zinc-800 p-8 text-center" data-testid="payment-status-card">
          {status === 'processing' && (
            <>
              <Loader2 className="w-16 h-16 text-orange-500 mx-auto mb-4 animate-spin" />
              <h2 className="text-2xl font-bold text-white mb-2">Confirming Payment</h2>
              <p className="text-zinc-400">{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" data-testid="payment-success-icon" />
              <h2 className="text-2xl font-bold text-white mb-2">
                {purchase?.type === 'firstlook'
                  ? (purchase.product === 'remote' ? 'Vehicle Decision Report Ordered!' : 'Full Inspection Booked!')
                  : 'Booking Confirmed!'}
              </h2>
              <p className="text-zinc-400 mb-4">{message}</p>
              {purchase?.type === 'firstlook' && (
                <p className="text-zinc-400 mb-6 text-sm" data-testid="firstlook-fulfillment-note">
                  {purchase.product === 'remote'
                    ? "Your full report is unlocked on your results page now. A CarLens specialist is reviewing and finalizing it, and you'll receive the finalized version by email the same day."
                    : "We'll contact you shortly to schedule your inspection at a time and place that works for you."}
                </p>
              )}
              <Button onClick={() => navigate('/')} className="bg-orange-500 hover:bg-orange-600 text-white">
                Return to Home
              </Button>
            </>
          )}

          {status === 'timeout' && (
            <>
              <CheckCircle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Almost There</h2>
              <p className="text-zinc-400 mb-6">{message}</p>
              <Button onClick={() => navigate('/')} className="bg-orange-500 hover:bg-orange-600 text-white">
                Return to Home
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center mx-auto mb-4">
                <span className="text-red-500 text-3xl">✕</span>
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Something Went Wrong</h2>
              <p className="text-zinc-400 mb-6">{message}</p>
              <Button onClick={() => navigate('/')} className="bg-orange-500 hover:bg-orange-600 text-white">
                Return to Home
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
